import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import * as AE from '../utils/AnalysisEngine';

/* ================= CONFIG & HELPERS ================= */

// Fix for default marker icon in leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const CarIcon = L.divIcon({
  html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#2563eb" stroke="white" stroke-width="2" class="w-10 h-10 drop-shadow-xl">
    <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
  </svg>`,
  className: "bg-transparent",
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  popupAnchor: [0, -20]
});

/* ================= COMPACT UI COMPONENTS ================= */

const CompactMetricCard = ({ title, value, unit, icon, analysis }) => {
  return (
    <div className="bg-white rounded-xl p-3 border border-slate-100 shadow-sm hover:shadow-md transition-all flex flex-col justify-between h-[100px] group">
      <div className="flex justify-between items-start">
        <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider truncate cursor-default" title={title}>{title}</span>
        {icon && <span className="text-slate-300 group-hover:text-blue-500 transition-colors text-xs">{icon}</span>}
      </div>

      <div className="flex flex-col">
        <div className="flex items-baseline gap-1">
          <span className="text-xl font-bold text-slate-800 tracking-tight font-urbanist">{value}</span>
          <span className="text-[10px] font-medium text-slate-500">{unit}</span>
        </div>
        {analysis && (
          <div className={`text-[9px] font-bold flex items-center gap-1 mt-1 ${analysis.color}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${analysis.color.replace('text', 'bg').split(' ')[0]}`}></span>
            {analysis.text}
          </div>
        )}
      </div>
    </div>
  );
};

const LiveStatBox = ({ label, value, unit, subtext }) => (
  <div className="bg-white px-5 py-3 rounded-2xl border border-slate-100 shadow-sm flex flex-col items-center justify-center min-w-[120px]">
    <span className="text-[10px] font-bold uppercase text-slate-400 tracking-widest">{label}</span>
    <div className="flex items-baseline gap-1 my-1">
      <span className="text-2xl font-black text-slate-800">{value}</span>
      <span className="text-xs font-bold text-slate-500">{unit}</span>
    </div>
    {subtext && <span className="text-[10px] font-semibold text-blue-500 bg-blue-50 px-2 py-0.5 rounded-full">{subtext}</span>}
  </div>
);

const MultiCarTable = ({ vehicles, setViewMode, setSelectedId }) => {
  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm text-slate-600">
          <thead className="bg-slate-50 text-slate-400 font-bold uppercase text-[10px] tracking-widest border-b border-slate-100">
            <tr>
              <th className="px-6 py-4">Vehicle</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4">KPH</th>
              <th className="px-6 py-4">Efficiency</th>
              <th className="px-6 py-4">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {vehicles.map((car) => {
              const speed = car.speed_ecu || car.speed || 0;
              const efficiency = car.fuel_rate && speed > 0 ? (speed / car.fuel_rate).toFixed(1) : '-';
              return (
                <tr key={car.docId} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 font-bold text-slate-800">{car.device_id || car.docId}</td>
                  <td className="px-6 py-4"><span className={`w-2 h-2 rounded-full inline-block mr-2 ${speed > 0 ? 'bg-emerald-500' : 'bg-slate-400'}`}></span>{speed > 0 ? 'Moving' : 'Idle'}</td>
                  <td className="px-6 py-4 font-mono font-bold">{speed}</td>
                  <td className="px-6 py-4 font-mono text-blue-600">{efficiency} <span className="text-[10px] text-slate-400">km/L</span></td>
                  <td className="px-6 py-4">
                    <button onClick={() => { setSelectedId(car.docId); setViewMode('single'); }} className="text-[10px] font-bold bg-slate-900 text-white px-3 py-1.5 rounded-lg hover:shadow-lg transition-all">TRACK</button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* ================= MAIN PAGE ================= */

/* ================= HISTORY COMPONENTS ================= */

const HistoryView = ({ vehicleId }) => {
  const [trips, setTrips] = useState([]);
  const [dailyStats, setDailyStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!vehicleId) return;

    // Fetch recent trips
    const q = collection(db, "iot_sim_cards", vehicleId, "trips");
    // In real app: query(collection(...), orderBy("startTime", "desc"), limit(20))
    // For demo, just listening to the collection
    const unsubTrips = onSnapshot(q, (snap) => {
      const fetchedTrips = [];
      snap.forEach(doc => fetchedTrips.push({ id: doc.id, ...doc.data() }));
      // Sort client-side if composte index missing
      fetchedTrips.sort((a, b) => (b.startTime?.seconds || 0) - (a.startTime?.seconds || 0));
      setTrips(fetchedTrips);
      setLoading(false);
    });

    return () => unsubTrips();
  }, [vehicleId]);

  return (
    <div className="animate-fade-in flex flex-col gap-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Daily Summary Card */}
        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-xl flex flex-col justify-between h-40 relative overflow-hidden group">
          <div className="absolute right-0 top-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
            <svg className="w-24 h-24 text-blue-600" fill="currentColor" viewBox="0 0 24 24"><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" /></svg>
          </div>
          <div>
            <span className="text-xs font-bold uppercase text-slate-400 tracking-widest">Today's Activity</span>
            <div className="text-3xl font-black text-slate-800 mt-2">{trips.filter(t => new Date(t.startTime.seconds * 1000).toDateString() === new Date().toDateString()).length} <span className="text-sm font-bold text-slate-400">TRIPS</span></div>
          </div>
          <div className="flex gap-4">
            <div>
              <span className="text-[10px] font-bold uppercase text-slate-400">Distance</span>
              <div className="font-mono font-bold text-blue-600">-- km</div>
            </div>
            <div>
              <span className="text-[10px] font-bold uppercase text-slate-400">Duration</span>
              <div className="font-mono font-bold text-blue-600">-- h</div>
            </div>
          </div>
        </div>

        {/* Monthly Summary */}
        <div className="bg-slate-900 p-6 rounded-3xl border border-slate-800 shadow-xl flex flex-col justify-between h-40 text-white relative overflow-hidden">
          <div className="absolute -right-4 -bottom-4 w-32 h-32 bg-blue-600/20 rounded-full blur-2xl"></div>
          <div>
            <span className="text-xs font-bold uppercase text-slate-500 tracking-widest">Monthly Insight</span>
            <div className="text-3xl font-black mt-2">-- <span className="text-sm font-bold text-slate-500">KM</span></div>
          </div>
          <div className="text-xs font-medium text-slate-400">
            Total distance driven this month.
          </div>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-50 bg-slate-50/50 flex justify-between items-center">
          <h3 className="text-xs font-bold uppercase text-slate-500 tracking-widest">Recent Trips</h3>
          <span className="bg-blue-100 text-blue-600 px-2 py-0.5 rounded text-[10px] font-bold">{trips.length} RECORDS</span>
        </div>
        <div className="divide-y divide-slate-50">
          {trips.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm font-medium">No trip history found.</div>
          ) : (
            trips.map(trip => {
              const start = trip.startTime ? new Date(trip.startTime.seconds * 1000) : new Date();
              const end = trip.endTime ? new Date(trip.endTime.seconds * 1000) : new Date();
              const duration = Math.round((end - start) / 1000 / 60); // minutes
              return (
                <div key={trip.id} className="p-4 hover:bg-slate-50 transition-colors flex items-center justify-between group">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${trip.status === 'active' ? 'bg-green-100 text-green-600 animate-pulse' : 'bg-slate-100 text-slate-400'}`}>
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                    </div>
                    <div>
                      <div className="font-bold text-slate-800 text-sm">{start.toLocaleDateString()} • {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                      <div className="text-xs font-medium text-slate-400 flex gap-2">
                        <span>{duration} min</span>
                        <span>•</span>
                        <span className={trip.status === 'active' ? 'text-green-500 font-bold' : ''}>{trip.status === 'active' ? 'DRIVING NOW' : 'Completed'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-6 text-right">
                    <div>
                      <div className="text-[10px] uppercase font-bold text-slate-400">Max Speed</div>
                      <div className="font-mono font-bold text-slate-700">{trip.maxSpeed || 0} km/h</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase font-bold text-slate-400">Data Pts</div>
                      <div className="font-mono font-bold text-slate-700">{trip.dataPoints || 0}</div>
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}

export default function IotTelemetry() {
  const [vehicles, setVehicles] = useState([]);
  const [viewMode, setViewMode] = useState('single');
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "iot_sim_cards"), (snapshot) => {
      const tempVehicles = [];
      snapshot.forEach((doc) => {
        tempVehicles.push({ docId: doc.id, ...doc.data() });
      });
      tempVehicles.sort((a, b) => a.docId.localeCompare(b.docId, undefined, { numeric: true, sensitivity: 'base' }));
      setVehicles(tempVehicles);

      if (!selectedId && tempVehicles.length > 0) {
        setSelectedId(tempVehicles[0].docId);
      }
      setLoading(false);
    });
    return () => unsub();
  }, [selectedId]);

  const activeVehicle = vehicles.find(v => v.docId === selectedId) || {};
  const getValue = (val, defaultVal = 0) => (val !== undefined && val !== null ? val : defaultVal);

  // --- DATA ---
  const speed = getValue(activeVehicle.speed_ecu || activeVehicle.speed);
  const rpm = getValue(activeVehicle.rpm);
  const coolant = getValue(activeVehicle.coolant_temp);
  const intake = getValue(activeVehicle.intake_air_temp);
  const ambient = getValue(activeVehicle.ambient_temp);
  const maf = getValue(activeVehicle.maf);
  const voltage = getValue(activeVehicle.ecu_voltage);
  const load = getValue(activeVehicle.engine_load);
  const runtime = getValue(activeVehicle.engine_runtime);
  const throttle = getValue(activeVehicle.throttle_pos || activeVehicle.throttle);
  const map = getValue(activeVehicle.map_kpa || activeVehicle.map);
  const baro = getValue(activeVehicle.baro_kpa || activeVehicle.baro);
  const stft = getValue(activeVehicle.stft);
  const ltft = getValue(activeVehicle.ltft);
  const fuelRate = getValue(activeVehicle.fuel_rate);
  const catalyst = getValue(activeVehicle.catalyst_temp);
  const lastSync = activeVehicle.receivedAt?.toDate ? activeVehicle.receivedAt.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '--:--';

  // --- CALCULATIONS ---
  const kmPerLiter = (fuelRate > 0 && speed > 5) ? (speed / fuelRate).toFixed(1) : "0.0";
  const estRange = (fuelRate > 0) ? Math.floor((45 * 0.5) / fuelRate * speed) : 450;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans p-4 md:p-6 pb-20 overflow-x-hidden">

      {/* 1. Header & Controls */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-xl font-black text-slate-900 tracking-tight font-urbanist flex items-center gap-2">
            <span className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white text-sm">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
            </span>
            TELEMETRY <span className="text-blue-600">PRO</span>
          </h1>
        </div>
        <div className="flex gap-2 bg-white p-1 rounded-lg border border-slate-200 shadow-sm">
          {['single', 'history', 'multiple'].map(mode => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${viewMode === mode ? 'bg-slate-900 text-white shadow' : 'text-slate-500 hover:bg-slate-50'}`}
            >
              {mode}
            </button>
          ))}
        </div>
      </div>

      {viewMode === 'history' && (
        <HistoryView vehicleId={selectedId} />
      )}

      {viewMode === 'single' && (
        <div className="flex flex-col gap-6 animate-fade-in">

          {/* 2. Top Section: LIVE DATA (High Priority) */}
          <div className="flex flex-wrap md:flex-nowrap gap-4 items-stretch">
            {/* Vehicle Select */}
            <div className="min-w-[200px] flex-shrink-0">
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                className="w-full h-full bg-white border border-slate-200 text-slate-800 font-bold rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500/20 shadow-sm appearance-none cursor-pointer"
              >
                {vehicles.map((v) => <option key={v.docId} value={v.docId}>{v.docId} ({v.device_id})</option>)}
              </select>
            </div>

            {/* Live Stats Row */}
            <div className="flex-grow flex gap-4 overflow-x-auto pb-2 md:pb-0 scrollType">
              <LiveStatBox label="Velocity" value={Math.round(speed)} unit="km/h" subtext={speed > 0 ? "MOVING" : "IDLE"} />
              <LiveStatBox label="Engine Speed" value={rpm} unit="RPM" />
              <LiveStatBox label="Efficiency" value={kmPerLiter} unit="km/L" />
              <LiveStatBox label="Est. Range" value={estRange} unit="km" subtext="~50% Fuel" />
              <div className="bg-slate-900 px-5 py-3 rounded-2xl border border-slate-800 shadow-lg flex flex-col items-center justify-center min-w-[120px] text-white ml-auto">
                <span className="text-[9px] font-bold uppercase text-slate-500 tracking-widest">Last Sync</span>
                <div className="text-xl font-mono font-bold mt-1">{lastSync}</div>
              </div>
            </div>
          </div>

          {/* 3. Middle Section: MAP (Full Width) */}
          <div className="w-full h-[350px] bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden relative z-0">
            {activeVehicle.latitude ? (
              <MapContainer center={[activeVehicle.latitude, activeVehicle.longitude]} zoom={16} zoomControl={false} style={{ height: '100%', width: '100%' }}>
                <TileLayer
                  url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                />
                <Marker position={[activeVehicle.latitude, activeVehicle.longitude]} icon={CarIcon}>
                  <Popup className="custom-popup"><strong>{activeVehicle.device_id}</strong></Popup>
                </Marker>
              </MapContainer>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-slate-400">
                <svg className="w-10 h-10 mb-2 animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                <span className="text-xs font-bold uppercase tracking-widest">Searching GPS Signal...</span>
              </div>
            )}
            {/* Map Overlay Badge */}
            <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur px-3 py-1.5 rounded-lg border border-slate-100 shadow-sm text-[10px] font-bold text-slate-500 z-[400]">
              LIVE TRACKING ACTIVE
            </div>
          </div>
          {/* 4. Bottom Section: Details Grid (Grouped) */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">

            {/* Group 1: Engine Core */}
            <div>
              <h3 className="text-[10px] font-bold uppercase text-slate-400 tracking-widest mb-3 pl-2 border-l-4 border-emerald-500">Engine & Power</h3>
              <div className="grid grid-cols-2 gap-3">
                <CompactMetricCard title="Coolant" value={coolant} unit="°C" analysis={AE.analyzeCoolant(coolant)} />
                <CompactMetricCard title="Battery" value={voltage} unit="V" analysis={AE.analyzeVoltage(voltage)} />
                <CompactMetricCard title="Load" value={load} unit="%" analysis={AE.analyzeEngineLoad(load)} />
                <CompactMetricCard title="Throttle" value={throttle} unit="%" />
              </div>
            </div>

            {/* Group 2: Air & Fuel */}
            <div>
              <h3 className="text-[10px] font-bold uppercase text-slate-400 tracking-widest mb-3 pl-2 border-l-4 border-blue-500">Air & Fuel</h3>
              <div className="grid grid-cols-2 gap-3">
                <CompactMetricCard title="MAF" value={maf} unit="g/s" />
                <CompactMetricCard title="Fuel Rate" value={fuelRate} unit="L/h" />
                <CompactMetricCard title="STFT" value={stft} unit="%" analysis={AE.analyzeFuelTrim(stft)} />
                <CompactMetricCard title="LTFT" value={ltft} unit="%" analysis={AE.analyzeFuelTrim(ltft)} />
              </div>
            </div>

            {/* Group 3: Environment */}
            <div>
              <h3 className="text-[10px] font-bold uppercase text-slate-400 tracking-widest mb-3 pl-2 border-l-4 border-amber-500">Environment</h3>
              <div className="grid grid-cols-2 gap-3">
                <CompactMetricCard title="Intake" value={intake} unit="°C" />
                <CompactMetricCard title="Ambient" value={ambient} unit="°C" />
                <CompactMetricCard title="MAP" value={map} unit="kPa" />
                <CompactMetricCard title="Baro" value={baro} unit="kPa" />
              </div>
            </div>

            {/* Group 4: Emissions & System */}
            <div>
              <h3 className="text-[10px] font-bold uppercase text-slate-400 tracking-widest mb-3 pl-2 border-l-4 border-purple-500">System</h3>
              <div className="grid grid-cols-2 gap-3">
                <CompactMetricCard title="Catalyst" value={catalyst} unit="°C" analysis={AE.analyzeCatalyst(catalyst)} />
                <div className="bg-slate-100 rounded-xl p-3 border border-slate-200 flex flex-col justify-center items-center h-[100px]">
                  <span className="text-[9px] font-bold uppercase text-slate-400">Runtime</span>
                  <span className="text-sm font-mono font-bold text-slate-600 mt-1">{AE.formatRuntime(runtime)}</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      )}

      {viewMode === 'multiple' && (
        <MultiCarTable vehicles={vehicles} setViewMode={setViewMode} setSelectedId={setSelectedId} />
      )}
    </div>
  );
}
